import { BORDER, FIELD, VIEW } from '../core/config.js';
import { BACKDROPS, UI } from './palette.js';

/**
 * The playfield floor and the cabinet frame around it.
 *
 * The floor texture is baked into an offscreen canvas once per round so the
 * per-frame cost stays at a single drawImage.
 */

const cache = new Map();

function buildFloor(index) {
  const tint = BACKDROPS[index % BACKDROPS.length];
  const canvas = document.createElement('canvas');
  canvas.width = VIEW.W;
  canvas.height = VIEW.H;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = tint.base;
  ctx.fillRect(0, 0, VIEW.W, VIEW.H);

  // Fine dot grid: reads as machined metal rather than empty black.
  ctx.fillStyle = tint.grid;
  for (let y = FIELD.top; y < VIEW.H; y += 8) {
    for (let x = FIELD.left; x < FIELD.right; x += 8) {
      ctx.fillRect(x, y, 1, 1);
    }
  }

  // Widely spaced diagonal hatching adds depth without becoming busy.
  ctx.strokeStyle = tint.accent;
  ctx.lineWidth = 1;
  ctx.globalAlpha = 0.5;
  ctx.save();
  ctx.beginPath();
  ctx.rect(FIELD.left, FIELD.top, FIELD.width, VIEW.H - FIELD.top);
  ctx.clip();
  ctx.beginPath();
  for (let x = FIELD.left - VIEW.H; x < FIELD.right + VIEW.H; x += 34) {
    ctx.moveTo(x, VIEW.H);
    ctx.lineTo(x + VIEW.H, 0);
  }
  ctx.stroke();
  ctx.restore();
  ctx.globalAlpha = 1;

  return canvas;
}

export function getFloor(index) {
  const key = index % BACKDROPS.length;
  if (!cache.has(key)) cache.set(key, buildFloor(key));
  return cache.get(key);
}

/**
 * Draw the segmented cabinet frame. Left, right and top only: the bottom is
 * open because that is where Vaus can lose the ball.
 */
export function drawFrame(ctx, { warpOpen = false, warpPhase = 0, paddleY = 0 } = {}) {
  const s = BORDER.side;
  const t = BORDER.top;

  ctx.fillStyle = UI.frameOuter;
  ctx.fillRect(0, 0, VIEW.W, t);
  ctx.fillRect(0, 0, s, VIEW.H);
  ctx.fillRect(VIEW.W - s, 0, s, VIEW.H);

  // Inner highlight edge facing the playfield.
  ctx.fillStyle = UI.frameInner;
  ctx.fillRect(s - 3, t - 3, VIEW.W - s * 2 + 6, 3);
  ctx.fillRect(s - 3, t - 3, 3, VIEW.H - t + 3);
  ctx.fillRect(VIEW.W - s, t - 3, 3, VIEW.H - t + 3);

  // Hard shadow line so the frame reads as raised.
  ctx.fillStyle = UI.frameEdge;
  ctx.fillRect(0, t, VIEW.W, 1);
  ctx.fillRect(s, t, 1, VIEW.H - t);
  ctx.fillRect(VIEW.W - s - 1, t, 1, VIEW.H - t);

  drawRivets(ctx);

  if (warpOpen) drawWarpGate(ctx, warpPhase, paddleY);
}

function drawRivets(ctx) {
  ctx.fillStyle = UI.rivet;
  const s = BORDER.side;
  for (let y = BORDER.top + 18; y < VIEW.H - 10; y += 42) {
    ctx.fillRect(s / 2 - 1, y, 3, 3);
    ctx.fillRect(VIEW.W - s / 2 - 2, y, 3, 3);
  }
  for (let x = 26; x < VIEW.W - 20; x += 42) {
    ctx.fillRect(x, BORDER.top / 2 - 2, 3, 3);
  }
}

/**
 * The Break capsule cuts a door in the right wall. Vaus escapes through it for
 * a bonus and an instant round skip.
 */
function drawWarpGate(ctx, phase, paddleY) {
  const s = BORDER.side;
  const height = 44;
  const y = paddleY - 14;
  const open = Math.min(1, phase);
  const gap = Math.round(height * open);

  ctx.fillStyle = '#05070a';
  ctx.fillRect(VIEW.W - s, y, s, gap);

  ctx.fillStyle = UI.accentAlt;
  ctx.fillRect(VIEW.W - s, y, 2, gap);
  ctx.fillRect(VIEW.W - s, y - 2, s, 2);
  ctx.fillRect(VIEW.W - s, y + gap, s, 2);

  // Travelling chevrons hint at the direction to escape.
  ctx.fillStyle = UI.accent;
  const t = (performance.now() / 90) % 12;
  for (let i = 0; i < 3; i += 1) {
    const cy = y + 8 + i * 12;
    if (cy > y + gap - 4) break;
    ctx.fillRect(VIEW.W - s + 3 + ((t + i * 4) % 10), cy, 4, 2);
  }
}
